<template>
  <div class="tab-pane sla-pane" :class="{ hidden: !active }">

    <!-- Empty: no SLA profiles configured -->
    <div v-if="slaLoaded && !profiles.length" class="atencao-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
      <p>Nenhum perfil de SLA configurado</p>
      <small>Configure perfis de SLA na página <router-link to="/sla">SLA</router-link> para monitorar acordos.</small>
    </div>

    <!-- Empty: no violations -->
    <div v-else-if="!slaItems.length" class="atencao-empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" style="color:#22c55e">
        <circle cx="12" cy="12" r="10"/><polyline points="20 6 9 17 4 12"/>
      </svg>
      <p style="color:#15803d">Todos os SLAs dentro do prazo</p>
      <small>Conversas em risco ou com SLA estourado aparecerão aqui.</small>
    </div>

    <template v-else>
      <!-- Summary -->
      <div class="sla-tab-summary">
        <span class="sla-sum-badge breach">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          {{ breachedCount }} SLA{{ breachedCount !== 1 ? 's' : '' }} estourado{{ breachedCount !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Cards grid -->
      <div class="sla-tab-grid">
        <div
          v-for="item in slaItems"
          :key="item.conv.id"
          :class="['sla-tcard', item.worstStatus]"
        >
          <!-- Top bar: fluxo + SLA profile + status badge -->
          <div :class="['sla-tcard-bar', item.worstStatus]">
            <span class="sla-tcard-fluxo">{{ fluxoLabel(item.conv.fluxo_id) }}</span>
            <span class="sla-tcard-profile">{{ item.profile.name }}</span>
            <span :class="['sla-tcard-status', item.worstStatus]">
              {{ item.worstStatus === 'breached' ? 'ESTOURADO' : 'EM RISCO' }}
            </span>
          </div>

          <!-- Contact header -->
          <div class="agrd-card-head">
            <div
              class="agrd-av"
              :style="{ background: avatarColor(item.conv.contact.name) }"
              @mouseenter="showAvPopup(item.conv, $event)"
              @mouseleave="emit('hidePopup')"
            >
              <img
                v-if="item.conv.contact.thumbnail"
                :src="item.conv.contact.thumbnail"
                :alt="item.conv.contact.name"
                style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block;"
                @error="(e) => (e.target as HTMLImageElement).style.display='none'"
              />
              <span v-else>{{ initials(item.conv.contact.name) }}</span>
            </div>
            <div class="agrd-contact">
              <div class="agrd-name">{{ item.conv.contact.name }}</div>
              <div v-if="item.conv.contact.phone" class="agrd-phone">{{ item.conv.contact.phone }}</div>
            </div>
            <div class="sla-head-right">
              <div v-if="resM(item)" :class="['sla-res-tag', resM(item)!.status]">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <span>{{ resM(item)!.status === 'breached' ? '+' + formatDur(-resM(item)!.remainingSecs) : formatDur(resM(item)!.remainingSecs) }}</span>
              </div>
              <span class="agrd-id">#{{ item.conv.id }}</span>
            </div>
          </div>

          <!-- Body -->
          <div class="agrd-card-body">
            <div class="agrd-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <span :class="{ 'agrd-muted': !item.conv.team }">{{ item.conv.team?.name || 'TypeBot' }}</span>
            </div>
            <div class="agrd-row">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              <span :class="{ 'agrd-muted': !item.conv.assignee }">{{ item.conv.assignee?.name || '— sem agente' }}</span>
            </div>

            <!-- SLA Metrics -->
            <div class="sla-tcard-metrics">
              <div
                v-for="m in item.metrics"
                :key="m.type"
                :class="['sla-mbadge', m.status]"
              >
                <div :class="['sla-mbadge-ico', m.status]">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                  </svg>
                </div>
                <div class="sla-mbadge-body">
                  <span class="sla-mbadge-val">
                    <template v-if="m.status === 'breached'">+{{ formatDur(-m.remainingSecs) }}</template>
                    <template v-else>{{ formatDur(m.remainingSecs) }}</template>
                  </span>
                  <span class="sla-mbadge-lbl">{{ m.label }}</span>
                </div>
              </div>
            </div>

            <div v-if="item.conv.labels.length" class="agrd-labels">
              <span v-for="lbl in item.conv.labels" :key="lbl" class="label-chip">{{ lbl.replace(/-|_/g, ' ') }}</span>
            </div>
          </div>

          <!-- Footer -->
          <div class="agrd-card-foot">
            <button
              :class="['sla-view-btn', item.worstStatus]"
              @click="requestOpenChat(item.conv.id)"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              Ver Conversa
            </button>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watchEffect } from 'vue'
import type { Conversation, PopupData } from '@/types/dashboard'
import { avatarColor, initials, fluxoLabel, fmtChannel } from '@/composables/useHelpers'
import { requestOpenChat } from '@/composables/useChatTrigger'
import { useSLA, type SLAProfile } from '@/composables/useSLA'

const props = defineProps<{
  conversations: Conversation[]
  active: boolean
}>()

const emit = defineEmits<{
  updateBadge: [count: number]
  showPopup: [data: PopupData, el: HTMLElement]
  hidePopup: []
}>()

const { profiles, slaLoaded, loadProfiles, getProfileForFluxo } = useSLA()

const now = ref(Math.floor(Date.now() / 1000))
let ticker: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  loadProfiles()
  ticker = setInterval(() => { now.value = Math.floor(Date.now() / 1000) }, 30_000)
})
onUnmounted(() => { if (ticker) clearInterval(ticker) })

// ── SLA Computation ──────────────────────────────────────
interface SLAMetric {
  type: 'firstResponse' | 'resolution' | 'redirect'
  label: string
  startTime: number
  deadline: number
  totalSecs: number
  status: 'breached' | 'atRisk' | 'ok'
  remainingSecs: number
}

interface SLAItem {
  conv: Conversation
  profile: SLAProfile
  metrics: SLAMetric[]
  worstStatus: 'breached' | 'atRisk'
}

function metricStatus(deadline: number, notifyBefore: number, nowSecs: number): 'breached' | 'atRisk' | 'ok' {
  if (nowSecs >= deadline) return 'breached'
  if (nowSecs >= deadline - notifyBefore * 60) return 'atRisk'
  return 'ok'
}

const slaItems = computed<SLAItem[]>(() => {
  const nowSecs = now.value
  return props.conversations
    .filter(c => (c.status === 'open' || c.status === 'pending') && !c.labels.includes('avaliacao_atendimento'))
    .flatMap(conv => {
      const profile = getProfileForFluxo(conv.fluxo_id)
      if (!profile) return []

      const metrics: SLAMetric[] = []

      if (profile.firstResponseTime > 0 && conv.waiting_since > 0) {
        const deadline = conv.waiting_since + profile.firstResponseTime * 60
        const st = metricStatus(deadline, profile.notifyBefore, nowSecs)
        if (st !== 'ok') metrics.push({
          type: 'firstResponse', label: '1ª Resposta',
          startTime: conv.waiting_since, deadline,
          totalSecs: profile.firstResponseTime * 60,
          status: st, remainingSecs: deadline - nowSecs,
        })
      }

      if (profile.resolutionTime > 0) {
        const deadline = conv.created_at + profile.resolutionTime * 60
        const st = metricStatus(deadline, profile.notifyBefore, nowSecs)
        if (st !== 'ok') metrics.push({
          type: 'resolution', label: 'Resolução',
          startTime: conv.created_at, deadline,
          totalSecs: profile.resolutionTime * 60,
          status: st, remainingSecs: deadline - nowSecs,
        })
      }

      if (profile.redirectTime > 0 && conv.waiting_since > 0) {
        const deadline = conv.waiting_since + profile.redirectTime * 60
        const st = metricStatus(deadline, profile.notifyBefore, nowSecs)
        if (st !== 'ok') metrics.push({
          type: 'redirect', label: 'Redirecionamento',
          startTime: conv.waiting_since, deadline,
          totalSecs: profile.redirectTime * 60,
          status: st, remainingSecs: deadline - nowSecs,
        })
      }

      if (!metrics.length) return []

      const worstStatus: 'breached' | 'atRisk' = metrics.some(m => m.status === 'breached') ? 'breached' : 'atRisk'
      if (worstStatus !== 'breached') return []
      return [{ conv, profile, metrics, worstStatus }]
    })
    .sort((a, b) => {
      if (a.worstStatus !== b.worstStatus) return a.worstStatus === 'breached' ? -1 : 1
      const aMin = Math.min(...a.metrics.map(m => m.remainingSecs))
      const bMin = Math.min(...b.metrics.map(m => m.remainingSecs))
      return aMin - bMin
    })
})

const breachedCount = computed(() => slaItems.value.length)

watchEffect(() => emit('updateBadge', slaItems.value.length))

function showAvPopup(conv: Conversation, e: MouseEvent) {
  emit('showPopup', {
    name:      conv.contact.name,
    role:      'Contato',
    phone:     conv.contact.phone || undefined,
    thumbnail: conv.contact.thumbnail || undefined,
    channel:   fmtChannel(conv.channel),
    team:      conv.team?.name || null,
    status:    conv.status,
    labels:    conv.labels,
  }, e.currentTarget as HTMLElement)
}

function getBarPct(m: SLAMetric): number {
  if (!m.startTime || !m.totalSecs) return 0
  const elapsed = now.value - m.startTime
  return Math.min(100, Math.max(0, (elapsed / m.totalSecs) * 100))
}

function formatDur(secs: number): string {
  if (secs < 0) secs = 0
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}h ${m}min`
  if (m > 0) return `${m}min ${s < 10 ? '0' : ''}${s}s`
  return `${s}s`
}

function resM(item: SLAItem): SLAMetric | null {
  return item.metrics.find(m => m.type === 'resolution') || null
}
</script>

<style scoped>
.sla-pane { padding: 20px; }

/* Summary */
.sla-tab-summary { display: flex; align-items: center; gap: 10px; margin-bottom: 18px; }
.sla-sum-badge {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 5px 14px; border-radius: 20px;
  font-size: 12.5px; font-weight: 700;
}
.sla-sum-badge svg { width: 13px; height: 13px; flex-shrink: 0; }
.sla-sum-badge.breach { background: rgba(239,68,68,.1); color: #b91c1c; border: 1px solid rgba(239,68,68,.2); }
.sla-sum-badge.risk   { background: rgba(249,115,22,.1); color: #c2410c; border: 1px solid rgba(249,115,22,.2); }

/* Grid */
.sla-tab-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 16px;
}

/* Card */
.sla-tcard {
  background: var(--white); border: 1px solid var(--ink-05);
  border-radius: var(--r); box-shadow: var(--shadow-sm); overflow: hidden;
  display: flex; flex-direction: column;
  transition: box-shadow var(--ease), transform var(--ease);
}
.sla-tcard:hover { box-shadow: var(--shadow-md); transform: translateY(-3px); }
.sla-tcard.breached { border-top: 3px solid #ef4444; }
.sla-tcard.atRisk   { border-top: 3px solid #f97316; }

/* Top bar */
.sla-tcard-bar {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 14px; border-bottom: 1px solid var(--ink-05);
}
.sla-tcard-bar.breached { background: rgba(239,68,68,.07); }
.sla-tcard-bar.atRisk   { background: rgba(249,115,22,.07); }

.sla-tcard-fluxo { font-size: 12px; font-weight: 700; color: var(--ink-80); flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.sla-tcard-profile { font-size: 11px; color: var(--ink-40); white-space: nowrap; }
.sla-tcard-status {
  font-size: 10px; font-weight: 800; padding: 2px 7px;
  border-radius: 4px; letter-spacing: .5px; flex-shrink: 0;
}
.sla-tcard-status.breached { background: rgba(239,68,68,.15); color: #b91c1c; }
.sla-tcard-status.atRisk   { background: rgba(249,115,22,.15); color: #c2410c; }

/* Resolution badge + head right */
.sla-head-right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
.sla-res-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 9px; border-radius: 20px;
  font-size: 11.5px; font-weight: 800; white-space: nowrap;
}
.sla-res-tag svg { flex-shrink: 0; }
.sla-res-tag.ok       { background: rgba(34,197,94,.12); color: #15803d; border: 1px solid rgba(34,197,94,.25); }
.sla-res-tag.atRisk   { background: rgba(249,115,22,.12); color: #c2410c; border: 1px solid rgba(249,115,22,.3); }
.sla-res-tag.breached { background: rgba(239,68,68,.12); color: #b91c1c; border: 1px solid rgba(239,68,68,.3); }

/* SLA metrics badges — grid (não flex-wrap): com 3 métricas (1ª resposta +
   resolução + redirecionamento), o texto mais longo do label ("Redirecionar")
   quebrava em 2 linhas só naquele item e deixava a fileira toda desalinhada/
   empilhada. auto-fit acomoda 1, 2 ou 3 métricas em colunas de largura igual,
   quebrando linha de forma limpa quando não cabem todas lado a lado. */
.sla-tcard-metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(110px, 1fr)); gap: 6px; margin-top: 4px; }
.sla-mbadge {
  display: flex; align-items: center; gap: 7px;
  padding: 6px 9px; border-radius: 8px; min-width: 0;
}
.sla-mbadge.ok       { background: rgba(34,197,94,.06); border: 1px solid rgba(34,197,94,.18); }
.sla-mbadge.atRisk   { background: rgba(249,115,22,.07); border: 1px solid rgba(249,115,22,.2); }
.sla-mbadge.breached { background: rgba(239,68,68,.07); border: 1px solid rgba(239,68,68,.2); }
.sla-mbadge-ico {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.sla-mbadge-ico.ok       { background: rgba(34,197,94,.15); color: #16a34a; }
.sla-mbadge-ico.atRisk   { background: rgba(249,115,22,.15); color: #ea580c; }
.sla-mbadge-ico.breached { background: rgba(239,68,68,.15); color: #dc2626; }
.sla-mbadge-body { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
.sla-mbadge-val  { font-size: 12.5px; font-weight: 800; line-height: 1.1; white-space: nowrap; }
.sla-mbadge-lbl  { font-size: 10px; font-weight: 500; color: var(--ink-40); }
.sla-mbadge.ok .sla-mbadge-val       { color: #15803d; }
.sla-mbadge.atRisk .sla-mbadge-val   { color: #c2410c; }
.sla-mbadge.breached .sla-mbadge-val { color: #b91c1c; }

/* Footer button */
.sla-view-btn {
  display: inline-flex; align-items: center; gap: 10px;
  border-radius: 999px; border: none;
  padding: 11px 24px; font-size: 13px; font-weight: 600; font-family: inherit;
  color: #fff; cursor: pointer;
  transition: background var(--ease), transform var(--ease), box-shadow var(--ease);
}
.sla-view-btn.breached { background: #ef4444; box-shadow: 0 2px 10px rgba(239,68,68,.3); }
.sla-view-btn.breached:hover { background: #dc2626; transform: translateY(-1px); }
.sla-view-btn.atRisk   { background: #f97316; box-shadow: 0 2px 10px rgba(249,115,22,.3); }
.sla-view-btn.atRisk:hover { background: #ea580c; transform: translateY(-1px); }
.sla-view-btn svg { width: 18px; height: 18px; flex-shrink: 0; }
</style>
